import React, { useEffect, useState } from 'react';
import AppIcon from "../../../components/ui/AppIcon";
import { openImageLightbox } from "../../../components/ui/ImageLightbox";
import PinnedMessageItem from "./PinnedMessageItem";
import AddMemberModal from './AddMemberModal';
import { useChat } from "../../../context/ChatContext";
import { useAuth } from "../../../context/AuthContext";
import { removeChannel } from '../../../services/channelService';
import toast from 'react-hot-toast';

export default function RightPanel({ onClose, onEdit }) {
    const { user } = useAuth();
    const { activeChat, pinnedMessages, channelFiles, fetchPinnedMessages, handleUnpinMessage, refreshData, onlineUsers, openUserProfile } = useChat();
    const [activeTab, setActiveTab] = useState('members');
    const role = user?.user_role?.role || user?.role || '';
    const isPrivileged = role === 'admin' || role === 'superadmin';
    const [showAddMemberModal, setShowAddMemberModal] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    const handleDeleteChannel = async () => {
        if (!activeChat?.data) return;
        try {
            await removeChannel(activeChat.data._id || activeChat.data.id);
            toast.success('Channel deleted');
            setConfirmDelete(false);
            refreshData();
            onClose();
        } catch (error) {
            console.error('Error deleting channel:', error);
            toast.error('Failed to delete channel');
        }
    };

    // Compute conversation ID: for channels use channel ID, for DMs use composite ID (sorted user IDs)
    const conversationId = activeChat && user ? (
        activeChat.type === "channel"
            ? (activeChat.data._id || activeChat.data.id)
            : [user._id || user.id, activeChat.data._id || activeChat.data.id].sort().join("_")
    ) : null;

    const channelPinnedMessages = conversationId ? (pinnedMessages[conversationId] || []) : [];
    const files = conversationId ? (channelFiles[conversationId] || []) : [];

    // Fetch pinned messages when active chat changes
    useEffect(() => {
        if (conversationId) {
            fetchPinnedMessages(conversationId);
        }
    }, [conversationId, fetchPinnedMessages]);

    if (!activeChat) return null;

    const dmUserId = activeChat.type === "dm" ? (activeChat.data._id || activeChat.data.id) : null;
    const isDmOnline = dmUserId ? onlineUsers.includes(dmUserId) : false;

    const info = {
        name: activeChat.data.name || activeChat.data.displayName || activeChat.data.username,
        description: activeChat.type === 'channel' ? activeChat.data.description : 'Direct Message',
        type: activeChat.type === 'channel' ? (activeChat.data.isPrivate ? 'private' : 'public') : 'dm',
        members: activeChat.type === 'channel' ? (activeChat.data.members || []) : [activeChat.data],
        pinnedMessage: activeChat.data.pinnedMessage,
        createdAt: activeChat.data.createdAt ? new Date(activeChat.data.createdAt).toLocaleDateString() : 'Recent'
    };

    const tabs = [
        { id: 'members', label: 'Members', icon: 'UsersIcon' },
        { id: 'pinned', label: 'Pinned', icon: 'PinIcon' },
        { id: 'files', label: 'Files', icon: 'FolderIcon' },
    ];

    return (
        <aside className="w-full h-full flex flex-col bg-app-surface border-l border-border-subtle overflow-hidden">

            {/* HEADER */}
            <div className="flex items-center justify-between px-6 h-[64px] border-b border-border-subtle shadow-subtle">
                <h3 className="text-[12px] font-bold text-text-primary uppercase tracking-widest">Details</h3>
                <button
                    onClick={onClose}
                    className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-black/5 text-text-muted hover:text-text-primary transition-all shadow-subtle"
                >
                    <AppIcon name="XMarkIcon" size={20} />
                </button>
            </div>

            {/* CHANNEL INFO MINI */}
            <div className="p-6 border-b border-border-subtle space-y-4 bg-app-bg/30">
                <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center text-3xl font-bold text-primary border border-primary/20 overflow-hidden shadow-subtle transition-transform hover:scale-105">
                        {activeChat.type === 'dm' ? (
                            <img src={activeChat.data.profilePhoto || `https://ui-avatars.com/api/?name=${encodeURIComponent(info.name)}`} className="w-full h-full object-cover" alt={info.name} />
                        ) : (
                            <span className="opacity-60">{info.type === 'private' ? '🔒' : '#'}</span>
                        )}
                    </div>
                    <div className="min-w-0">
                        <h4 className="font-display text-[17px] font-semibold text-text-primary truncate">{activeChat.type === 'channel' ? '#' : ''}{info.name}</h4>
                        {activeChat.type === 'dm' ? (
                            <div className="flex items-center gap-1.5 mt-0.5">
                                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${isDmOnline ? "bg-volt" : "bg-ink-meta"}`} />
                                <span className="eyebrow eyebrow-10">
                                    {isDmOnline ? "Online" : "Offline"}
                                </span>
                            </div>
                        ) : (
                            <p className="eyebrow eyebrow-10 mt-0.5">Created {info.createdAt}</p>
                        )}
                    </div>
                </div>
                {/* <div className="flex gap-2">
                    <button
                        onClick={onEdit}
                        className="flex-1 px-4 py-2.5 bg-app-surface hover:bg-black/5 rounded-xl text-[11px] font-bold text-text-primary transition-all border border-border-subtle uppercase tracking-widest shadow-subtle"
                    >
                        Settings
                    </button>
                    <button className="px-3 py-2.5 bg-app-surface hover:bg-black/5 text-text-muted hover:text-text-primary rounded-xl transition-all border border-border-subtle shadow-subtle">
                        <AppIcon name="BellIcon" size={16} />
                    </button>
                </div> */}
            </div>

            {/* TABS NAVIGATION */}
            <div className="px-4 pt-4">
                <div className="flex p-1 gap-1 bg-black/[0.04] border border-border-subtle">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex-1 flex flex-col items-center gap-1 py-1.5 transition-colors ${activeTab === tab.id
                                ? 'bg-app-surface text-ink border border-border-subtle'
                                : 'text-text-muted hover:text-text-primary hover:bg-black/5'
                                }`}
                        >
                            <AppIcon name={tab.icon} size={15} />
                            <span className="text-[9px] font-mono font-medium uppercase tracking-[0.1em]">{tab.label}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* TAB CONTENT */}
            <div className="flex-1 overflow-y-auto px-6 py-6 custom-scrollbar">

                {activeTab === 'members' && (
                    <div className="space-y-4 animate-fade-in">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-[10px] font-bold text-text-muted tracking-widest uppercase">Members — {info.members.length}</span>
                            {activeChat.type === 'channel' && isPrivileged && (
                                <button
                                    onClick={() => setShowAddMemberModal(true)}
                                    className="text-[10px] font-bold text-primary hover:text-primary-dark uppercase tracking-wider"
                                >
                                    Add
                                </button>
                            )}
                        </div>
                        {info.members.map((member, idx) => (
                            <MemberRow 
                                key={member._id || member.id || idx} 
                                member={member} 
                                onlineUsers={onlineUsers}
                                onProfileClick={() => {
                                    const memberId = member._id || member.id;
                                    if (memberId) {
                                        openUserProfile(memberId);
                                    }
                                }}
                            />
                        ))}
                    </div>
                )}

                {activeTab === 'pinned' && (
                    <div className="space-y-3 animate-fade-in">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-[10px] font-bold text-text-muted tracking-widest uppercase">Pinned Messages</span>
                            <span className="text-[10px] font-bold text-primary">{channelPinnedMessages.length}</span>
                        </div>
                        {channelPinnedMessages.length > 0 ? (
                            <div className="space-y-3 ">
                                {channelPinnedMessages.map(msg => (
                                    <PinnedMessageItem
                                        key={msg._id || msg.id}
                                        message={msg}
                                        onUnpin={async () => {
                                            const msgId = msg._id || msg.id;
                                            await handleUnpinMessage(msgId, conversationId);
                                        }}
                                        onClick={() => {
                                            const element = document.querySelector(`[data-message-id="${msg._id || msg.id}"]`);
                                            if (element) {
                                                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                            }
                                        }}
                                    />
                                ))}
                            </div>
                        ) : (
                            <p className="text-[11px] text-text-muted italic">No pinned messages yet.</p>
                        )}
                    </div>
                )}

                {activeTab === 'files' && (
                    <div className="space-y-3 animate-fade-in">
                        <div className="flex items-center justify-between mb-2">
                            <span className="eyebrow eyebrow-10">Shared Files</span>
                            <span className="text-[11px] text-ink font-semibold">{files.length}</span>
                        </div>
                        {files.length > 0 ? (
                            <div className="space-y-3">
                                {files.map(file => (
                                    <FileItem
                                        key={file._id || file.id}
                                        file={file}
                                        onClick={() => {
                                            const url = file.text || file.message || file.content;
                                            if (!url) return;
                                            // images open in the in-app viewer; other files open in a new tab
                                            if (/\.(jpg|jpeg|png|gif|webp|svg|bmp|avif)(\?|$)/i.test(url)) {
                                                openImageLightbox(url, file.fileName || 'image');
                                            } else {
                                                window.open(url, '_blank');
                                            }
                                        }}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center h-20 opacity-20">
                                <AppIcon name="FolderIcon" size={32} />
                                <p className="text-[10px] font-bold uppercase tracking-widest mt-2">No files shared</p>
                            </div>
                        )}
                    </div>
                )}

            </div>

            {/* FOOTER ACTIONS */}
            {activeChat.type === 'channel' && isPrivileged && (
                <div className="p-6 border-t border-border-subtle mt-auto">
                    {confirmDelete ? (
                        <div className="space-y-2">
                            <p className="text-[10px] text-center text-text-muted font-bold uppercase tracking-widest">
                                Delete <span className="text-error">#{activeChat.data.name}</span>? This cannot be undone.
                            </p>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setConfirmDelete(false)}
                                    className="flex-1 py-2.5 rounded-2xl text-[10px] font-bold uppercase tracking-widest border border-border-subtle text-text-muted hover:bg-black/5 transition-all"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDeleteChannel}
                                    className="flex-1 py-2.5 rounded-2xl bg-error text-white text-[10px] font-bold uppercase tracking-widest hover:opacity-90 transition-all"
                                >
                                    Confirm Delete
                                </button>
                            </div>
                        </div>
                    ) : (
                        <button
                            onClick={() => setConfirmDelete(true)}
                            className="w-full py-3 rounded-2xl bg-error/5 hover:bg-error text-error hover:text-white text-[10px] font-bold uppercase tracking-widest transition-all border border-error/10 hover:shadow-lg hover:shadow-error/20"
                        >
                            Delete Channel
                        </button>
                    )}
                </div>
            )}
            {showAddMemberModal && activeChat?.type === 'channel' && (
                <AddMemberModal
                    channel={activeChat.data}
                    onClose={() => setShowAddMemberModal(false)}
                    onSuccess={refreshData}
                />
            )}
        </aside>
    );
}

function MemberRow({ member, onlineUsers = [], onProfileClick }) {
    const statusColors = {
        online: "bg-online",
        away: "bg-away",
        offline: "bg-text-muted"
    };

    const memberId = member._id || member.id;
    const status = onlineUsers.includes(memberId) ? 'online' : 'offline';

    const handleClick = () => {
        onProfileClick?.();
    };

    return (
        <div className="flex items-center gap-3.5 p-2.5 -mx-1 rounded-xl hover:bg-black/5 cursor-pointer transition-all group border border-transparent hover:border-border-subtle hover:shadow-subtle"
            onClick={handleClick}
        >
            <div className="relative">
                <img src={member.profilePhoto || `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name || member.displayName || 'User')}`} className="w-10 h-10 rounded-xl object-cover border border-border-subtle shadow-subtle" alt={member.name} />
                <div className={`absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-app-surface rounded-full flex items-center justify-center border-2 border-app-surface shadow-sm`}>
                    <div className={`w-2 h-2 rounded-full ${statusColors[status] || 'bg-text-muted'}`} />
                </div>
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-[13px] font-bold text-text-primary truncate">{member.name || member.displayName || member.username}</p>
                <p className="text-[10px] text-text-muted font-bold truncate uppercase tracking-widest opacity-60">
                    {member.role || member.user_role?.role || (status === 'online' ? 'Active' : 'Offline')}
                </p>
            </div>
            <AppIcon name="UserCircleIcon" size={16} className="text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
    );
}

// ── Helper: Get file icon based on extension ───────────────────────────────────
const getFileIcon = (filename) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    const iconMap = {
        pdf: 'DocumentIcon',
        doc: 'DocumentTextIcon',
        docx: 'DocumentTextIcon',
        xls: 'TableCellsIcon',
        xlsx: 'TableCellsIcon',
        zip: 'ArchiveBoxIcon',
        rar: 'ArchiveBoxIcon',
        jpg: 'PhotoIcon',
        jpeg: 'PhotoIcon',
        png: 'PhotoIcon',
        gif: 'PhotoIcon',
        mp4: 'VideoCameraIcon',
        mov: 'VideoCameraIcon',
        webm: 'VideoCameraIcon',
        default: 'DocumentIcon'
    };
    return iconMap[ext] || iconMap.default;
};

function FileItem({ file, onClick }) {
    const url = file.text || file.message || file.content || '';
    const fileName = url.split('/').pop() || 'File';

    const getSenderName = () => {
        if (file.senderName) return file.senderName;
        if (file.sender && typeof file.sender === 'object') {
            return file.sender.name || file.sender.displayName || file.sender.username;
        }
        return file.sender || "User";
    };

    const formatDate = (ts) => {
        if (!ts) return "";
        try {
            return new Date(ts).toLocaleDateString([], { month: 'short', day: 'numeric' });
        } catch (e) {
            return "";
        }
    };

    return (
        <div
            className="flex items-center gap-3 p-3 rounded-2xl bg-app-surface border border-border-subtle hover:border-primary/40 transition-all cursor-pointer group shadow-subtle hover:shadow-premium"
            onClick={onClick}
        >
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary flex-shrink-0 group-hover:bg-primary group-hover:text-white transition-all shadow-sm">
                <AppIcon name={getFileIcon(fileName)} size={20} />
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-[13px] font-bold text-text-primary truncate">{fileName}</p>
                <p className="text-[10px] text-text-muted font-bold tracking-widest uppercase opacity-60">
                    {getSenderName()} • {formatDate(file.timestamp || file.createdAt)}
                </p>
            </div>
            <AppIcon name="ArrowDownTrayIcon" size={16} className="text-text-muted opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
    );
}